/**
 * LoginPage — v2 redesign (Phase 13 of the Rebirth migration).
 *
 * Replaces the legacy Wiz-style red-gradient hero with the v2 navy + deep
 * teal language used by the rest of the platform (DashboardLayoutV2 sidebar,
 * v2 Button, v2 Pill, TrustChip, etc.). Uses v2 design tokens via
 * `var(--v2-*)` so the page automatically inherits any future token tweak.
 *
 * Functionality is preserved 1:1: same login / register / mfa /
 * forgot-password / reset-sent flow, same handlers, same auth context
 * calls. Only chrome and palette changed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye, EyeOff, ArrowLeft, Lock, Mail, CheckCircle2,
  KeyRound, Cloud,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/* Why a person was sent back to the login screen. api.js appends ?reason=
 * when the backend rejects a token against the per-user invalidation
 * watermark. Module scope, not component scope: it is a constant, and
 * keeping it here means the effect that reads it has no dependencies to
 * declare. Anything unrecognised renders no notice rather than a guess. */
const RETURN_REASONS = {
  signed_out: 'You signed out. Sign in again to continue.',
  role_changed: 'Your role changed. Sign in again to pick up your new permissions.',
  scope_changed: 'Your active scope changed in another window. Sign in again to continue here.',
  account_changed: 'Your account access changed. Sign in again to continue.',
  session_ended: 'Your session is no longer valid. Sign in again to continue.',
};
import { useToast } from '../contexts/ToastContext';
import { ssoCallback as exchangeSsoCallback } from '../services/auth';
import {
  validatePassword, getPasswordRequirements, getPasswordStrength,
  PASSWORD_STRENGTH_LABELS, PASSWORD_STRENGTH_COLORS,
} from '../utils/passwordPolicy';

/* Display metadata per provider kind; the enabled set itself comes from the server. */
const PROVIDER_META = {
  ENTRA:        { name: 'Microsoft', Icon: Cloud },
  GOOGLE:       { name: 'Google',    Icon: Mail },
  OKTA:         { name: 'Okta',      Icon: KeyRound },
  GENERIC_OIDC: { name: 'Single sign-on', Icon: KeyRound },
};

/* Hero stats — same outcome metrics, restyled for v2 navy hero. */
const HERO_STATS = [
  { value: '94%',    label: 'Faster triage' },
  { value: '73%',    label: 'Cost reduction' },
  { value: '< 2 min', label: 'Mean response' },
  { value: '24 / 7',  label: 'Protected ops' },
];

const TRUST_SIGNALS = [
  'SOC 2 Type II',
  'RBAC + Zero Trust',
  'Gated Lifecycle',
  'Hash-chain Audit',
];

/* ═══════════════════════════════════════════════════════════════════
   v2 token shortcuts. CSS variables are loaded by `styles/index.css`
   on every page (login included). We read them through `var(--v2-…)`
   in inline styles so any future token tweak takes effect for free.
   ─────────────────────────────────────────────────────────────────── */
const T = {
  ink:        'var(--v2-text-primary)',
  inkSec:     'var(--v2-text-secondary)',
  inkMuted:   'var(--v2-text-muted)',
  page:       'var(--v2-bg-base)',
  surface:    'var(--v2-bg-surface)',
  border:     'var(--v2-border-default)',
  borderSub:  'var(--v2-border-subtle)',
  action:     'var(--v2-action)',
  actionStr:  'var(--v2-action-strong)',
  actionSoft: 'var(--v2-action-soft)',
  actionGlow: 'var(--v2-action-glow)',
  actionDark: 'var(--v2-action-on-dark)',
  brand:      'var(--v2-brand)',
  brandSoft:  'var(--v2-brand-soft)',
  navy:       'var(--v2-bg-sidebar)',
  navyHov:    'var(--v2-bg-sidebar-hover)',
  invInk:     'var(--v2-text-inverse)',
  invInk2:    'var(--v2-text-inverse-secondary)',
  invInk3:    'var(--v2-text-inverse-muted)',
  shadow:     'var(--v2-shadow-md)',
  shadowLg:   'var(--v2-shadow-lg)',
  ease:       'var(--v2-ease)',
  duration:   'var(--v2-duration)',
};

/* ═══════════════════════════════════════════════════════════════════
   HERO PANEL — navy gradient with subtle teal grid overlay.
   Module-level so it doesn't remount on every render.
   ─────────────────────────────────────────────────────────────────── */
const HeroPanel = () => {
  /* Hidden below md (<768px) so the sign-in form takes the full viewport width on
   * phones instead of being crushed beside a 46%-width marketing hero. */
  const [hidden, setHidden] = React.useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 767px)').matches
  );
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setHidden(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  if (hidden) return null;
  return (
  <div style={{
    flex: '0 0 46%', minHeight: '100vh',
    background: `linear-gradient(165deg, ${T.navy} 0%, #0a1830 50%, #07142a 100%)`,
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '60px 56px', position: 'relative', overflow: 'hidden',
    color: T.invInk,
  }}>
    {/* Decorative ambience */}
    <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(44,217,197,0.18), transparent 60%)' }} />
    <div style={{ position: 'absolute', bottom: -120, left: -60, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(44,217,197,0.10), transparent 60%)' }} />
    <div style={{ position: 'absolute', inset: 0, opacity: 0.06,
      backgroundImage: 'radial-gradient(rgba(255,255,255,.6) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
    }} />

    <div style={{ position: 'relative', zIndex: 1, maxWidth: 480 }}>
      {/* Brand lockup — Prove7 three-dot mark + wordmark.
       * The dots use the canonical brand palette (coral · periwinkle ·
       * deep teal); the same mark is used in the in-app sidebar. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 56 }}>
        {/* Wordmark only, matching the in-app sidebar and the API portal. */}
        <div>
          {/* Same lockup as the in-app sidebar: stylized wordmark, -light variant
            * for the navy gradient behind it. */}
          <img
            src="/brand/wordmark-light.png"
            alt="Prove7 Control Vector"
            style={{ width: 132, height: 'auto', display: 'block' }}
          />
          <div style={{ fontSize: 9.5, color: T.invInk3, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 6 }}>
            Control Vector
          </div>
        </div>
      </div>

      {/* Headline + sub */}
      <h1 style={{
        fontSize: 38, fontWeight: 800, color: T.invInk,
        lineHeight: 1.12, letterSpacing: '-0.025em', margin: '0 0 18px 0',
      }}>
        Every AI agent.<br />
        <span style={{ color: T.actionDark }}>Earned trust.</span><br />
        Provable protection.
      </h1>
      <p style={{
        fontSize: 14.5, color: T.invInk2,
        lineHeight: 1.6, margin: '0 0 44px 0', maxWidth: 420,
      }}>
        Protect every AI agent across your enterprise — with deterministic policy
        enforcement, autonomy gates, and a hash-chained audit trail at every
        decision.
      </p>

      {/* Stat cards — frosted glass on navy */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 36 }}>
        {HERO_STATS.map((s) => (
          <div key={s.label} style={{
            padding: '16px 18px', borderRadius: 12,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(6px)',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.invInk, letterSpacing: '-0.02em', marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.invInk3, fontWeight: 500, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Trust signal pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {TRUST_SIGNALS.map((t) => (
          <span key={t} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 999,
            background: 'rgba(44,217,197,0.08)',
            border: '1px solid rgba(44,217,197,0.22)',
            color: T.invInk2,
            fontSize: 11, fontWeight: 600, letterSpacing: '0.01em',
          }}>
            <CheckCircle2 size={11} color={T.actionDark} aria-hidden="true" />
            {t}
          </span>
        ))}
      </div>
    </div>
  </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Inputs — v2 surface, deep teal focus ring.
   ─────────────────────────────────────────────────────────────────── */
const INPUT_BASE = {
  width: '100%', padding: '11px 14px', borderRadius: 10,
  border: `1px solid ${T.border}`, background: T.surface,
  color: T.ink, fontSize: 14, fontFamily: 'inherit',
  boxSizing: 'border-box', transition: `border-color ${T.duration}, box-shadow ${T.duration}`,
  outline: 'none',
};
const INPUT_FOCUS = {
  borderColor: T.action,
  boxShadow: `0 0 0 3px ${T.actionGlow}`,
};

const FocusInput = React.memo(({ style: extraStyle, ...props }) => {
  const [focused, setFocused] = React.useState(false);
  return (
    <input
      {...props}
      style={{ ...INPUT_BASE, ...extraStyle, ...(focused ? INPUT_FOCUS : {}) }}
      onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
    />
  );
});

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ─────────────────────────────────────────────────────────────────── */
export default function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyMfa, ssoLogin, ssoDiscover, register, forgotPassword, isDevMode } = useAuth();
  const { addToast } = useToast(); // eslint-disable-line no-unused-vars

  // Where to go after a successful sign-in. Honours a ?returnTo= set when the person was sent here
  // mid-flow (e.g. authorizing an application), but ONLY for safe in-app paths — a single leading
  // slash, never a protocol-relative "//host" or absolute URL — so it can't be used as an open
  // redirect. Falls back to the Smart Agent Wizard, the surface everyone starts on.
  const postLoginTarget = () => {
    const raw = new URLSearchParams(window.location.search).get('returnTo');
    if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw;
    // Everyone lands on the Smart Agent Wizard. The full platform is one link away
    // from there, and this changes nobody's entitlements -- RequireModuleRoute
    // (App.jsx:376) still applies on arrival. A returnTo still wins, so a deep link
    // into the platform is unaffected.
    return '/agents/smartwizard';
  };

  /* Which form opens, seeded from the URL.
   *
   * A stranger arriving from "Score Your Agent" has no account, and landing them on a password
   * field for one they do not have is the wrong first impression — so the link that sends them
   * here can ask for the register form directly.
   *
   * Only 'register' is honoured. The other views this page can be in are reached by doing
   * something: 'mfa' follows a correct password, 'forgot' follows a deliberate click. Letting a
   * URL put the page into either would mean a link could show someone a code field with nothing
   * behind it, so the allowlist is one value long on purpose. */
  const [view, setView] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('view');
      return v === 'register' ? 'register' : 'login';
    } catch (_) { return 'login'; }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regMobile, setRegMobile] = useState('');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [mfaRefs] = useState(Array(6).fill(null).map(() => React.createRef()));
  const [forgotEmail, setForgotEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [returnNotice, setReturnNotice] = useState(null);
  useEffect(() => {
    try {
      const reason = new URLSearchParams(window.location.search).get('reason');
      if (reason && RETURN_REASONS[reason]) setReturnNotice(RETURN_REASONS[reason]);
    } catch { /* no notice rather than a broken login page */ }
  }, []);

  /* ── Shared styles ─────────────────────────────────────────────── */
  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: T.inkMuted, marginBottom: 6, letterSpacing: '0.06em',
    textTransform: 'uppercase',
  };

  const primaryBtnStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 10,
    border: 'none',
    background: T.action,
    color: '#ffffff', fontSize: 14, fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1,
    fontFamily: 'inherit',
    boxShadow: `0 4px 14px ${T.actionGlow}`,
    transition: `all ${T.duration} ${T.ease}`,
    letterSpacing: '0.01em',
  };

  const secondaryBtnStyle = {
    width: '100%', padding: '11px 16px', borderRadius: 10,
    border: `1px solid ${T.border}`, background: T.surface,
    color: T.inkSec, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    transition: `all ${T.duration} ${T.ease}`,
  };

  const linkStyle = {
    background: 'none', border: 'none', color: T.action,
    fontSize: 13, cursor: 'pointer', padding: 0,
    fontFamily: 'inherit', fontWeight: 600,
  };

  const errorBox = error ? (
    <div role="alert" style={{
      padding: '10px 14px', marginBottom: 16, background: 'var(--v2-danger-soft)',
      border: '1px solid var(--v2-danger)', borderRadius: 10, fontSize: 13, color: 'var(--v2-danger)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span aria-hidden="true" style={{ fontWeight: 800 }}>!</span> {error}
    </div>
  ) : null;

  /* Neutral, not an error: being signed out is not a failure. */
  const noticeBox = returnNotice ? (
    <div role="status" style={{
      padding: '10px 14px', marginBottom: 16, background: T.actionSoft,
      border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 13, color: T.inkSec,
    }}>
      {returnNotice}
    </div>
  ) : null;

  const successBox = successMsg ? (
    <div style={{
      padding: '10px 14px', marginBottom: 16, background: T.actionSoft,
      border: `1px solid ${T.action}`, borderRadius: 10, fontSize: 13, color: T.actionStr,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <CheckCircle2 size={14} aria-hidden="true" /> {successMsg}
    </div>
  ) : null;

  /* ── Handlers ──────────────────────────────────────────────────── */
  const handleLogin = async () => {
    if (!email.trim()) { setError('Email is required'); return; }
    if (!password) { setError('Password is required'); return; }
    setError(null); setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (!result.ok) setError(result.error || 'Login failed');
      else if (result.mfaRequired) setView('mfa');
      else navigate(postLoginTarget());
    } catch { setError('Unexpected error'); }
    finally { setLoading(false); }
  };

  const handleRegister = async () => {
    if (!regFirstName.trim() || !regLastName.trim()) { setError('First and last name required'); return; }
    if (!regEmail.trim()) { setError('Email is required'); return; }
    const pwResult = validatePassword(regPassword);
    if (!pwResult.valid) { setError(`Password: ${pwResult.errors[0]}`); return; }
    if (regPassword !== regConfirmPassword) { setError('Passwords do not match'); return; }
    setError(null); setLoading(true);
    try {
      const result = await register(regFirstName.trim(), regLastName.trim(), regEmail.trim(), regPassword, regMobile.trim());
      if (!result.ok) setError(result.error || 'Registration failed');
      else { setSuccessMsg('Registration successful! Please log in.'); setView('login'); setEmail(regEmail); }
    } catch { setError('Registration failed'); }
    finally { setLoading(false); }
  };

  const handleMfaInput = (idx, val) => {
    if (val.length > 1) val = val.slice(-1);
    if (val && !/^\d$/.test(val)) return;
    const next = [...mfaCode]; next[idx] = val; setMfaCode(next);
    if (val && idx < 5) mfaRefs[idx + 1]?.current?.focus();
  };

  const handleMfaKey = (idx, e) => {
    if (e.key === 'Backspace' && !mfaCode[idx] && idx > 0) mfaRefs[idx - 1]?.current?.focus();
    if (e.key === 'Enter') handleMfaSubmit();
  };

  const handleMfaSubmit = async () => {
    const code = mfaCode.join('');
    if (code.length < 6) { setError('Enter all 6 digits'); return; }
    setError(null); setLoading(true);
    try {
      const result = await verifyMfa(code, 'totp');
      if (!result.ok) setError(result.error || 'Verification failed');
      else navigate(postLoginTarget());
    } catch { setError('Unexpected error'); }
    finally { setLoading(false); }
  };

  const handleSsoMessage = useCallback(async (event) => {
    if (event.origin !== window.location.origin) return;
    if (!event.data || event.data.type !== 'SSO_CALLBACK') return;
    const { provider, code, state } = event.data;
    setLoading(true); setError(null);
    try {
      const result = await exchangeSsoCallback(provider, code, state);
      if (!result.ok) setError(result.error || 'SSO authentication failed');
      else if (result.mfaRequired) setView('mfa');
      else window.location.href = '/';
    } catch { setError('SSO callback failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleSsoMessage);
    return () => window.removeEventListener('message', handleSsoMessage);
  }, [handleSsoMessage]);

  // Home Realm Discovery — a work email's domain resolves which sign-in
  // provider(s) serve it. No always-on provider buttons: an organization's
  // sign-on only appears once its own users identify themselves by email,
  // which keeps each tenant's federation private to that tenant.
  const [ssoProviders, setSsoProviders] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const discoveredForRef = React.useRef('');

  const runDiscovery = useCallback(async (rawEmail) => {
    const value = (rawEmail || '').trim().toLowerCase();
    const domain = value.includes('@') ? value.split('@')[1] : '';
    // Only worth a lookup once the address carries a domain.
    if (!domain || !domain.includes('.')) {
      discoveredForRef.current = '';
      setSsoProviders([]);
      return;
    }
    if (discoveredForRef.current === domain) return;   // already resolved this domain
    discoveredForRef.current = domain;
    setDiscovering(true);
    try {
      const result = await ssoDiscover(value);
      const list = result.ok && Array.isArray(result.providers) ? result.providers : [];
      setSsoProviders(list.map((p) => {
        const meta = PROVIDER_META[p.kind] || { name: p.displayName || 'Single sign-on', Icon: KeyRound };
        return { id: p.id, kind: p.kind, name: p.displayName || meta.name, Icon: meta.Icon };
      }));
    } catch {
      setSsoProviders([]);
    } finally {
      setDiscovering(false);
    }
  }, [ssoDiscover]);

  const handleSso = async (provider) => {
    setError(null); setLoading(true);
    try {
      sessionStorage.setItem('sso_provider', provider.id);
      const result = await ssoLogin(provider.id);
      if (!result.ok) setError(result.error || 'Single sign-on is unavailable right now');
      else if (result.mfaRequired) setView('mfa');
      else if (result.authorizationUrl) {
        const w = 500, h = 650;
        const l = window.screenX + (window.outerWidth - w) / 2;
        const t = window.screenY + (window.outerHeight - h) / 2;
        window.open(result.authorizationUrl, 'sso-popup', `width=${w},height=${h},left=${l},top=${t},resizable=yes,scrollbars=yes`);
      } else navigate(postLoginTarget());
    } catch { setError('Single sign-on is unavailable right now'); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) { setError('Email is required'); return; }
    setError(null); setLoading(true);
    try {
      const result = await forgotPassword(forgotEmail.trim());
      if (!result.ok) setError(result.error || 'Request failed');
      else setView('resetSent');
    } catch { setError('Request failed'); }
    finally { setLoading(false); }
  };

  const resetState = () => { setError(null); setSuccessMsg(null); setMfaCode(['', '', '', '', '', '']); };

  /* ── Layout wrappers ───────────────────────────────────────────── */
  const pageWrap = {
    display: 'flex', minHeight: '100vh',
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    background: T.page,
  };
  const formPanelOuter = {
    flex: 1, minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: T.page, padding: '40px 24px',
  };
  const formPanelInner = { width: '100%', maxWidth: 420 };

  /* ═══════════════════════════════════════════════════════════════
     LOGIN VIEW
     ─────────────────────────────────────────────────────────────── */
  if (view === 'login') {
    return (
      <div style={pageWrap}>
        <HeroPanel />
        <div style={formPanelOuter}><div style={formPanelInner}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
              Welcome back
            </h2>
            <p style={{ fontSize: 14, color: T.inkMuted, margin: 0 }}>
              Sign in to your protection control plane
            </p>
          </div>

          {isDevMode && (
            <div style={{
              padding: '10px 14px', marginBottom: 16, background: T.actionSoft,
              border: `1px solid ${T.action}`, borderRadius: 10, fontSize: 12, color: T.actionStr,
            }}>
              <strong>Dev Mode</strong> — Backend unreachable. Use any dev email + password "demo".
            </div>
          )}

          {noticeBox}
          {errorBox}
          {successBox}

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Email</label>
            <FocusInput
              type="email" value={email} autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => runDiscovery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { runDiscovery(email); handleLogin(); } }}
              placeholder="you@company.com"
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <FocusInput
                type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Enter your password"
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  border: 'none', cursor: 'pointer', color: T.inkMuted,
                  padding: 6, display: 'inline-flex', alignItems: 'center',
                }}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 24, textAlign: 'right' }}>
            <button type="button" style={linkStyle} onClick={() => { resetState(); setView('forgot'); }}>
              Forgot password?
            </button>
          </div>

          <button
            type="button"
            onClick={handleLogin} disabled={loading} style={primaryBtnStyle}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = T.actionStr; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 20px ${T.actionGlow}`; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.action; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 4px 14px ${T.actionGlow}`; }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          {discovering && ssoProviders.length === 0 && (
            <div style={{ textAlign: 'center', fontSize: 12, color: T.inkMuted, marginTop: 16 }}>
              Checking your organization's sign-on…
            </div>
          )}

          {ssoProviders.length > 0 && (<>
          {/* Home Realm Discovery — provider(s) resolved from the work email above */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
            <div style={{ flex: 1, height: 1, background: T.borderSub }} />
            <span style={{ fontSize: 11, color: T.inkMuted, fontWeight: 600, letterSpacing: '0.04em' }}>SINGLE SIGN-ON FOR YOUR ORGANIZATION</span>
            <div style={{ flex: 1, height: 1, background: T.borderSub }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(ssoProviders.length, 3)}, 1fr)`, gap: 10, marginBottom: 28 }}>
            {ssoProviders.map((p) => {
              const Icon = p.Icon;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSso(p)}
                  style={{
                    padding: '12px 10px', borderRadius: 10,
                    border: `1px solid ${T.border}`, background: T.surface,
                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    color: T.inkSec, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 6,
                    transition: `all ${T.duration} ${T.ease}`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.action; e.currentTarget.style.color = T.actionStr; e.currentTarget.style.background = T.actionSoft; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.inkSec; e.currentTarget.style.background = T.surface; }}
                >
                  <Icon size={18} aria-hidden="true" />
                  {`Continue with ${p.name}`}
                </button>
              );
            })}
          </div>
          </>)}

          <div style={{ textAlign: 'center', fontSize: 13, color: T.inkMuted }}>
            Don't have an account?{' '}
            <button type="button" style={{ ...linkStyle, fontWeight: 700 }} onClick={() => { resetState(); setView('register'); }}>
              Register
            </button>
          </div>
        </div></div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     REGISTER VIEW
     ─────────────────────────────────────────────────────────────── */
  if (view === 'register') {
    return (
      <div style={pageWrap}>
        <HeroPanel />
        <div style={formPanelOuter}><div style={formPanelInner}>
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
              Create account
            </h2>
            <p style={{ fontSize: 14, color: T.inkMuted, margin: 0 }}>
              Join the Protected Execution Layer
            </p>
          </div>

          {errorBox}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>First name</label>
              <FocusInput value={regFirstName} onChange={(e) => setRegFirstName(e.target.value)} placeholder="John" />
            </div>
            <div>
              <label style={labelStyle}>Last name</label>
              <FocusInput value={regLastName} onChange={(e) => setRegLastName(e.target.value)} placeholder="Doe" />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Email</label>
            <FocusInput type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="you@company.com" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Mobile (optional)</label>
            <FocusInput type="tel" value={regMobile} onChange={(e) => setRegMobile(e.target.value)} placeholder="+1 (555) 123-4567" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Password</label>
            <FocusInput type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="Letter + number + special char" />
            {regPassword && (
              <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ display: 'flex', gap: 2, flex: 1 }}>
                  {[1,2,3,4].map((i) => (
                    <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: getPasswordStrength(regPassword) >= i ? PASSWORD_STRENGTH_COLORS[getPasswordStrength(regPassword)] : T.borderSub }} />
                  ))}
                </div>
                <span style={{ fontSize: 10, color: PASSWORD_STRENGTH_COLORS[getPasswordStrength(regPassword)], fontWeight: 700 }}>
                  {PASSWORD_STRENGTH_LABELS[getPasswordStrength(regPassword)]}
                </span>
              </div>
            )}
            <div style={{ fontSize: 10, color: T.inkMuted, marginTop: 3 }}>{getPasswordRequirements()}</div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Confirm password</label>
            <FocusInput
              type="password" value={regConfirmPassword}
              onChange={(e) => setRegConfirmPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
              placeholder="Re-enter password"
            />
            {regConfirmPassword && regConfirmPassword !== regPassword && (
              <div style={{ fontSize: 10, color: 'var(--v2-danger)', marginTop: 3 }}>Passwords do not match</div>
            )}
          </div>

          <button type="button" onClick={handleRegister} disabled={loading} style={primaryBtnStyle}
            onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = T.actionStr; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.action; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {loading ? 'Creating account…' : 'Create account'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: T.inkMuted }}>
            Already have an account?{' '}
            <button type="button" style={{ ...linkStyle, fontWeight: 700 }} onClick={() => { resetState(); setView('login'); }}>Sign in</button>
          </div>
        </div></div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     MFA VIEW
     ─────────────────────────────────────────────────────────────── */
  if (view === 'mfa') {
    return (
      <div style={pageWrap}>
        <HeroPanel />
        <div style={formPanelOuter}><div style={formPanelInner}>
          <div style={{ marginBottom: 8 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: T.actionSoft,
              border: `1px solid ${T.action}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 20,
            }}>
              <Lock size={24} color={T.action} aria-hidden="true" />
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
              Verify identity
            </h2>
            <p style={{ fontSize: 14, color: T.inkMuted, marginBottom: 28 }}>
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          {errorBox}

          <div style={{ display: 'flex', gap: 8, marginBottom: 28, justifyContent: 'center' }}>
            {mfaCode.map((digit, idx) => (
              <input
                key={idx}
                ref={mfaRefs[idx]}
                value={digit}
                onChange={(e) => handleMfaInput(idx, e.target.value)}
                onKeyDown={(e) => handleMfaKey(idx, e)}
                maxLength={1}
                inputMode="numeric"
                aria-label={`Digit ${idx + 1} of 6`}
                style={{
                  width: 50, height: 56, borderRadius: 10,
                  border: digit ? `2px solid ${T.action}` : `1px solid ${T.border}`,
                  background: digit ? T.actionSoft : T.surface,
                  color: T.ink, fontSize: 22, fontWeight: 700,
                  textAlign: 'center', fontFamily: 'inherit',
                  outline: 'none', transition: `all ${T.duration} ${T.ease}`,
                  boxShadow: digit ? `0 0 0 3px ${T.actionGlow}` : 'none',
                }}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={handleMfaSubmit}
            disabled={loading || mfaCode.join('').length < 6}
            style={{
              ...primaryBtnStyle,
              opacity: (loading || mfaCode.join('').length < 6) ? 0.4 : 1,
              cursor: (loading || mfaCode.join('').length < 6) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Verifying…' : 'Verify & continue'}
          </button>

          <button
            type="button"
            onClick={() => { resetState(); setView('login'); }}
            style={{ ...secondaryBtnStyle, marginTop: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = T.actionSoft; e.currentTarget.style.borderColor = T.action; e.currentTarget.style.color = T.actionStr; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.surface; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.inkSec; }}
          >
            <ArrowLeft size={14} aria-hidden="true" /> Back to login
          </button>
        </div></div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     FORGOT PASSWORD VIEW
     ─────────────────────────────────────────────────────────────── */
  if (view === 'forgot') {
    return (
      <div style={pageWrap}>
        <HeroPanel />
        <div style={formPanelOuter}><div style={formPanelInner}>
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '0 0 6px 0', letterSpacing: '-0.02em' }}>
              Reset password
            </h2>
            <p style={{ fontSize: 14, color: T.inkMuted, margin: 0 }}>
              Enter your email to receive a reset link
            </p>
          </div>

          {errorBox}

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Email</label>
            <FocusInput
              type="email" value={forgotEmail} autoComplete="email"
              onChange={(e) => setForgotEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
              placeholder="you@company.com"
            />
          </div>

          <button type="button" onClick={handleForgotPassword} disabled={loading} style={primaryBtnStyle}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = T.actionStr; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.action; }}
          >
            {loading ? 'Sending…' : 'Send reset link'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button type="button" style={{ ...linkStyle, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => { resetState(); setView('login'); }}>
              <ArrowLeft size={12} aria-hidden="true" /> Back to login
            </button>
          </div>
        </div></div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     RESET SENT
     ─────────────────────────────────────────────────────────────── */
  if (view === 'resetSent') {
    return (
      <div style={pageWrap}>
        <HeroPanel />
        <div style={formPanelOuter}><div style={formPanelInner}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16,
              background: T.actionSoft, border: `1px solid ${T.action}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Mail size={28} color={T.action} aria-hidden="true" />
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>
              Check your email
            </h2>
            <p style={{ fontSize: 14, color: T.inkMuted, margin: '0 0 28px 0' }}>
              If an account exists with that email, we've sent a password reset link.
            </p>
          </div>
          <button type="button" onClick={() => { resetState(); setView('login'); }} style={primaryBtnStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = T.actionStr; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.action; }}
          >
            Back to login
          </button>
        </div></div>
      </div>
    );
  }

  return null;
}
